// src/modules/communications/pages/PlaceholderLeadDetailPage.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  FaUser, 
  FaQuestionCircle, 
  FaPhone, 
  FaSms, 
  FaSpinner,
  FaCalendarAlt,
  FaArrowLeft,
  FaPlus,
  FaEnvelope,
  FaMapMarkerAlt,
  FaEdit,
  FaSave,
  FaTimes
} from 'react-icons/fa';
import { 
  getPlaceholderLeadCommunications, 
  upgradePlaceholderLead 
} from '../services/communicationService';
import DashboardLayout from '../../../layouts/DashboardLayout';
import { formatPhoneNumber, formatDate } from '../utils/formatUtils';
import { useCommunication } from '../context/CommunicationContext';
import { useNavigate, useParams } from 'react-router-dom';
import CommunicationHistory from '../components/CommunicationHistory';
import Modal from '../../../components/shared/Modal';
import Input from '../../../components/shared/Input';
import TextArea from '../../../components/shared/TextArea';

// Styled Components
const PageContainer = styled.div`
  padding: ${props => props.theme.spacing(2)};
`;

const BackLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  margin-bottom: ${props => props.theme.spacing(3)};
  
  &:hover {
    text-decoration: underline;
  }
`;

const LeadCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(3)};
  margin-bottom: ${props => props.theme.spacing(3)};
`;

const LeadHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing(3)};
`;

const LeadTitle = styled.h1`
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  
  svg {
    color: ${props => props.theme.colors.warning};
  }
`;

const PlaceholderBadge = styled.span`
  font-size: 0.8rem;
  padding: 3px 10px;
  border-radius: 12px;
  background: ${props => props.theme.colors.warning}33;
  color: ${props => props.theme.colors.warning};
  font-weight: normal;
  margin-left: ${props => props.theme.spacing(1)};
`;

const LeadContact = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing(1)};
  margin-bottom: ${props => props.theme.spacing(3)};
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  font-size: ${props => props.theme.typography.fontSize.medium};
  
  svg {
    color: ${props => props.theme.colors.primary};
  }
`;

const LeadStats = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(3)};
  margin-bottom: ${props => props.theme.spacing(3)};
  
  .stat {
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing(1)};
    color: ${props => props.theme.colors.textSecondary};
    
    svg {
      color: ${props => props.theme.colors.primary};
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(2)};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  padding: ${props => props.theme.spacing(1.5)} ${props => props.theme.spacing(3)};
  background: ${props => props.primary ? props.theme.colors.primary : props.theme.colors.surface};
  color: ${props => props.primary ? '#fff' : props.theme.colors.textPrimary};
  border: 1px solid ${props => props.primary ? props.theme.colors.primary : props.theme.colors.inputGray};
  border-radius: ${props => props.theme.borderRadius};
  cursor: pointer;
  font-size: ${props => props.theme.typography.fontSize.small};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.primary ? props.theme.colors.primary : props.theme.colors.primary + '22'};
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    font-size: 1rem;
  }
`;

const TabsContainer = styled.div`
  margin-bottom: ${props => props.theme.spacing(3)};
`;

const Tabs = styled.div`
  display: flex;
  border-bottom: 1px solid ${props => props.theme.colors.inputGray}33;
  margin-bottom: ${props => props.theme.spacing(2)};
`;

const Tab = styled.button`
  padding: ${props => props.theme.spacing(1.5)} ${props => props.theme.spacing(3)};
  background: none;
  border: none;
  border-bottom: 3px solid ${props => props.active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  
  svg {
    font-size: 2rem;
    color: ${props => props.theme.colors.primary};
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ModalFormGroup = styled.div`
  margin-bottom: ${props => props.theme.spacing(2)};
`;

const ModalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${props => props.theme.spacing(2)};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

/**
 * Placeholder Lead Detail Page - shows details about a specific unknown contact
 * @returns {React.ReactElement} Placeholder Lead Detail Page component
 */
const PlaceholderLeadDetailPage = () => {
  const { leadId } = useParams();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('communications');
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    street: '',
    city: '',
    state: '',
    zipcode: '',
    notes: ''
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  
  const { openCallModal, openSmsComposer, showNotification } = useCommunication();
  const navigate = useNavigate();
  
  // Fetch lead details
  useEffect(() => {
    const fetchLead = async () => {
      setLoading(true);
      try {
        const data = await getPlaceholderLeadCommunications(leadId);
        setLead(data.lead);
        setError(null);
        
        // Initialize form data
        setFormData({
          name: data.lead.name.replace('Unknown Call ', '') || '',
          email: '',
          street: '',
          city: '',
          state: '',
          zipcode: '',
          notes: ''
        });
      } catch (err) {
        console.error('Error fetching placeholder lead:', err);
        setError('Failed to load contact details');
      } finally {
        setLoading(false);
      }
    };
    
    fetchLead();
  }, [leadId]);
  
  // Handle action buttons
  const handleCall = () => {
    if (lead) {
      openCallModal(lead.phone, lead.id, lead.name);
    }
  };
  
  const handleSms = () => {
    if (lead) {
      openSmsComposer(lead.phone, lead.id, lead.name);
    }
  };
  
  // Handle form data change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle form submission
  const handleUpgradeLead = async () => {
    setFormLoading(true);
    setFormError(null);
    
    try {
      const result = await upgradePlaceholderLead(leadId, formData);
      
      showNotification('Contact successfully upgraded to customer', 'success');
      setUpgradeModalOpen(false);
      
      // Navigate to the new lead page
      navigate(`/leads/view/${result.lead.id}`);
    } catch (err) {
      console.error('Error upgrading lead:', err);
      setFormError('Failed to upgrade contact. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };
  
  if (loading && !lead) {
    return (
      <DashboardLayout>
        <LoadingContainer>
          <FaSpinner />
        </LoadingContainer>
      </DashboardLayout>
    );
  }
  
  if (error) {
    return (
      <DashboardLayout>
        <PageContainer>
          <BackLink href="/communications/placeholder-leads">
            <FaArrowLeft /> Back to Unknown Contacts
          </BackLink>
          <div>{error}</div>
        </PageContainer>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <PageContainer>
        <BackLink href="/communications/placeholder-leads">
          <FaArrowLeft /> Back to Unknown Contacts
        </BackLink>
        
        {lead && (
          <>
            {/* Lead Info Card */}
            <LeadCard>
              <LeadHeader>
                <LeadTitle>
                  <FaQuestionCircle />
                  {lead.name}
                  <PlaceholderBadge>Unknown Contact</PlaceholderBadge>
                </LeadTitle>
              </LeadHeader>
              
              <LeadContact>
                <ContactItem>
                  <FaPhone />
                  {formatPhoneNumber(lead.phone)}
                </ContactItem>
                <ContactItem>
                  <FaCalendarAlt />
                  First contact: {formatDate(lead.createdAt)}
                </ContactItem>
              </LeadContact>
              
              <ActionButtons>
                <ActionButton primary onClick={() => setUpgradeModalOpen(true)}>
                  <FaPlus /> Convert to Customer
                </ActionButton>
                
                <ActionButton onClick={handleCall}>
                  <FaPhone /> Call
                </ActionButton>
                
                <ActionButton onClick={handleSms}>
                  <FaSms /> Message
                </ActionButton>
              </ActionButtons>
            </LeadCard>
            
            {/* Tabs */}
            <TabsContainer>
              <Tabs>
                <Tab 
                  active={activeTab === 'communications'} 
                  onClick={() => setActiveTab('communications')}
                >
                  <FaPhone /> Communications
                </Tab>
              </Tabs>
              
              {activeTab === 'communications' && (
                <CommunicationHistory leadId={leadId} />
              )}
            </TabsContainer>
            
            {/* Upgrade Modal */}
            <Modal
              title="Convert to Customer"
              isOpen={upgradeModalOpen}
              onCancel={() => setUpgradeModalOpen(false)}
              onConfirm={handleUpgradeLead}
              confirmText={formLoading ? 'Converting...' : 'Convert to Customer'}
              confirmDisabled={formLoading}
            >
              <p>Add contact information to convert this unknown contact to a customer.</p>
              
              {formError && (
                <div style={{ color: 'red', marginBottom: '1rem' }}>{formError}</div>
              )}
              
              <ModalFormGroup>
                <Input
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  icon={<FaUser />}
                  required
                />
              </ModalFormGroup>
              
              <ModalFormGroup>
                <Input
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  icon={<FaEnvelope />}
                  required
                />
              </ModalFormGroup>
              
              <ModalFormGroup>
                <Input
                  label="Street Address"
                  name="street"
                  value={formData.street}
                  onChange={handleChange}
                  icon={<FaMapMarkerAlt />}
                />
              </ModalFormGroup>
              
              <ModalGrid>
                <Input
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  icon={<FaMapMarkerAlt />}
                />
                
                <Input
                  label="State"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  icon={<FaMapMarkerAlt />}
                />
                
                <Input
                  label="Zipcode"
                  name="zipcode"
                  value={formData.zipcode}
                  onChange={handleChange}
                  icon={<FaMapMarkerAlt />}
                />
              </ModalGrid>
              
              <ModalFormGroup>
                <TextArea
                  label="Notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Add any notes about this customer..."
                />
              </ModalFormGroup>
            </Modal>
          </>
        )}
      </PageContainer>
    </DashboardLayout>
  );
};

export default PlaceholderLeadDetailPage;